import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as zod from "zod";
import { Button } from "../../../components/button.jsx";
import React, { useEffect, useRef } from "react";
import { AttachmentsTab } from "./documentEditPage/attachmentsTab/attachmentsTab.jsx";
import { TabsGroup } from "../../../components/tabsGroup.jsx";
import { InfoTab } from "./documentEditPage/infoTab.jsx";
import { LoaderFiller } from "../../../components/loaderFiller.jsx";
import { TransactionsTab } from "./documentEditPage/transactionsTab.jsx";
import { useResource } from "../../../hooks/useResource.js";
import { useParams } from "react-router";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

export let DOCUMENT_EDIT_PANEL = "editDocument";

export function DocumentEditPage() {
  let { id: documentId } = useParams();
  let session = useSelector((state) => state.session);
  let navigate = useNavigate();

  let ref = useRef();
  let documentsService = useResource("documents");
  const onSave = async (data) => {
    return documentsService.edit(documentId, data).then((saved) => {
      if (documentId === "new") {
        navigate("/documents/" + saved.id, { replace: true });
      } else {
        reset(saved);
      }
    });
  };
  const onRemove = (d) => {
    return documentsService.delete(documentId).then(() => {
      navigate("/documents");
    });
  };

  let { control, handleSubmit, setValue, watch, formState, reset } = useForm({
    resolver: zodResolver(
      zod.object({
        reference: zod.string().nullish(),
        date: zod.string().nullish(),
        currency: zod.string().nullish(),
        amount: zod.number().nullish(),
        type: zod.enum(["charge", "invoice", "quote", "other"]).nullish(),
        uploadIds: zod.array(zod.number()).nullish(),
        notes: zod
          .array(
            zod.object({
              when: zod.string(),
              content: zod.string(),
              id: zod.string(),
            })
          )
          .nullish(),
        contact: zod.object({ id: zod.number() }).passthrough().nullish(),
        transactions: zod
          .array(zod.object({ id: zod.number() }).passthrough())
          .nullish(),

        lines: zod
          .array(
            zod.object({
              quantity: zod.number(),
              id: zod.number(),
              unitPrice: zod.number(),
              name: zod.string(),
            })
          )
          .nullish(),

        taxes: zod
          .array(
            zod.object({
              name: zod.string(),
              id: zod.number(),
              amount: zod.string(),
            })
          )
          .nullish(),
      })
    ),
  });

  useEffect(() => {
    if (documentId !== "new") {
      documentsService.get(documentId).then((res) => reset(res));
    } else {
      reset({ id: "new", currency: session.account.defaultCurrency });
    }
  }, [documentId]);

  useEffect(() => {
    if (!ref.current) return;

    let listener = (e) => {
      if (watch("lines")?.length > 0) {
        let linesAmount = watch("lines")
          .map((line) => line.quantity * line.unitPrice)
          .reduce((a, b) => a + b, 0);

        let taxesAmount = (watch("taxes") || [])
          .map((tax) => {
            if (tax.amount?.toString().includes("%")) {
              return (tax.amount.replace("%", "") / 100) * linesAmount;
            } else {
              return tax.amount * 1;
            }
          })
          .reduce((a, b) => a + b, 0);

        setValue("amount", linesAmount + taxesAmount);
      }
    };
    ref.current.addEventListener("keyup", listener);

    listener();
    return () =>
      ref.current && ref.current.removeEventListener("keyup", listener);
  }, [document]);

  return (
    <div className={"flex gap-3 flex-col h-full p-3"}>
      <div className={"flex gap-3"}>
        <h2 className={"font-bold py-2 flex items-center flex-grow"}>
          Documents
        </h2>

        <Button type={"link"} to={"/documents"}>
          <span className={"fa fa-arrow-left"} />{" "}
        </Button>
      </div>

      <form
        className={"flex flex-col gap-3 flex-grow overflow-auto relative"}
        onSubmit={handleSubmit((d) => onSave(d))}
        ref={ref}
      >
        {!watch("id") && <LoaderFiller />}
        <TabsGroup
          tabs={[
            {
              label: "Info",
              icon: "pen",
              component: (
                <InfoTab watch={watch} control={control} setValue={setValue} />
              ),
            },
            ["invoice", "charge"].includes(watch("type"))
              ? {
                  label: `Transactions (${
                    (watch("transactions") || []).length
                  })`,
                  icon: "arrow-right-arrow-left",
                  component: (
                    <TransactionsTab watch={watch} setValue={setValue} />
                  ),
                }
              : {},
            {
              label: `Attachments (${(watch("uploadIds") || []).length})`,
              icon: "paperclip",
              component: <AttachmentsTab watch={watch} setValue={setValue} />,
            },
          ]}
          afterTabs={
            <>
              <Button
                type={"submit"}
                variant={
                  Object.keys(formState.dirtyFields).length
                    ? "danger"
                    : "primary"
                }
                onClick={handleSubmit((d) => onSave(d))}
              >
                Save
              </Button>
              <Button onClick={onRemove} variant={"alert"}>
                Delete
              </Button>
            </>
          }
        />
        <button type={"submit"} style={{ display: "hidden" }} />
      </form>
    </div>
  );
}
